import { useMemo, useState } from 'react';
import {
  useAllCustomerUsers,
  useCustomersLite,
  useRevokeCustomerUser,
  useInviteCustomerUser,
  type CustomerUserRow,
  type DisableReason,
} from '../hooks/useCustomerProvisioning';
import { describeJobError } from '../lib/triggerTask';
import { portalViewAsUrl } from '../lib/portalUrl';
import { formatDateTime } from '../lib/formatters';
import { useAuth } from '../contexts/AuthContext';
import { canManageCustomerAccess } from '../lib/customerAccess';

type RowFeedback = { tone: 'success' | 'error'; message: string };
type StatusFilter = 'all' | CustomerUserRow['status'];

const INPUT =
  'w-full min-h-[44px] md:min-h-[40px] rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary';

export function PortalAccessPage() {
  const { role } = useAuth();
  const canManage = canManageCustomerAccess(role);
  const { data: rows, isLoading, isError } = useAllCustomerUsers();
  const { data: customers } = useCustomersLite();
  const revoke = useRevokeCustomerUser();
  const invite = useInviteCustomerUser();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState<Record<string, RowFeedback>>({});
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [action, setAction] = useState<{ type: 'invite' | 'revoke'; row: CustomerUserRow } | null>(null);
  const [reason, setReason] = useState('');

  const customerNameById = useMemo(() => {
    const m = new Map<string, string>();
    (customers ?? []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [customers]);

  const filtered = useMemo(() => {
    let list = rows ?? [];
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (customerFilter !== 'all') list = list.filter((r) => r.customer_id === customerFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.user.email.toLowerCase().includes(q) ||
          (r.user.full_name ?? '').toLowerCase().includes(q) ||
          (customerNameById.get(r.customer_id) ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [rows, statusFilter, customerFilter, search, customerNameById]);

  const setRowFeedback = (id: string, value: RowFeedback | null) => {
    setFeedback((prev) => {
      const next = { ...prev };
      if (value) next[id] = value;
      else delete next[id];
      return next;
    });
  };

  const handleResend = async (row: CustomerUserRow) => {
    if (!canManage || row.status !== 'invited') return;
    setBusyRow(row.id);
    setRowFeedback(row.id, null);
    try {
      const result = await invite.mutateAsync({
        customer_id: row.customer_id,
        reason, expected_version: row.access_version,
        email: row.user.email,
        full_name: row.user.full_name ?? undefined,
        role: 'viewer',
        is_primary_contact: row.is_primary_contact,
        contact_id: row.contact_id ?? undefined,
        zoho_contact_id: row.zoho_contact_id ?? undefined,
      });
      setRowFeedback(row.id, {
        tone: 'success',
        message: result.runId ? `Invitation processing queued (run ${result.runId}). Existing users can sign in to accept.` : 'Invitation processing queued. Existing users can sign in to accept.',
      });
    } catch (e) {
      setRowFeedback(row.id, { tone: 'error', message: describeJobError(e) });
    } finally {
      setBusyRow(null);
    }
  };

  const handleRevoke = async (row: CustomerUserRow) => {
    if (!canManage) return;
    setBusyRow(row.id);
    setRowFeedback(row.id, null);
    try {
      await revoke.mutateAsync({ customer_user_id: row.id, expected_version: row.access_version, reason });
      setRowFeedback(row.id, { tone: 'success', message: 'Access revoked.' });
    } catch (e) {
      setRowFeedback(row.id, { tone: 'error', message: describeJobError(e) });
    } finally {
      setBusyRow(null);
    }
  };

  const total = rows?.length ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text">Portal Access</h1>
        <p className="text-sm text-text-muted mt-1">
          Every portal login and its customer accounts. Invitations are processed in the background; revocation takes effect as soon as it is confirmed.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-surface p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block">
          <span className="sr-only">Search by email, name or customer</span>
          <input
            type="search"
            placeholder="Email, name or customer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={INPUT}
          />
        </label>
        <label className="block">
          <span className="sr-only">Filter by customer</span>
          <select value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} className={INPUT}>
            <option value="all">All customers</option>
            {(customers ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="sr-only">Filter by status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)} className={INPUT}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="disabled">Disabled</option>
            <option value="revoked">Revoked</option>
          </select>
        </label>
        <p className="text-sm text-text-muted self-center" aria-live="polite">
          {filtered.length} of {total} {total === 1 ? 'link' : 'links'}
        </p>
      </div>

      {isError && (
        <div role="alert" className="rounded-lg border border-border bg-surface p-4 text-sm text-notification">
          Could not load portal access. Refresh to try again.
        </div>
      )}

      {!canManage && <p className="text-sm text-text-muted">Access changes require an admin or superadmin.</p>}
      {action && canManage && (
        <section role="dialog" aria-modal="false" aria-labelledby="access-action-title" className="rounded-lg border border-border bg-surface p-4 space-y-3">
          <h2 id="access-action-title" className="font-semibold text-text">{action.type === 'revoke' ? 'Revoke portal access' : 'Confirm pending invitation'}</h2>
          <p className="text-sm text-text-muted">{action.row.user.email} — {customerNameById.get(action.row.customer_id) ?? 'Customer'}</p>
          {action.type === 'invite' && <p className="text-sm text-text-muted">Existing users can sign in to accept their invitation. This action does not send another email.</p>}
          <label className="block text-sm text-text">Reason
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength={5} maxLength={1000} rows={3} className={INPUT} disabled={busyRow !== null} />
          </label>
          <p className="text-xs text-text-muted">Enter 5 to 1000 characters explaining this access change.</p>
          <div className="flex gap-2">
            <button type="button" disabled={busyRow !== null || reason.trim().length < 5} className="min-h-11 rounded-md bg-primary px-3 text-text-inverse disabled:opacity-50" onClick={async () => {
              if (action.type === 'revoke') await handleRevoke(action.row); else await handleResend(action.row);
              setAction(null); setReason('');
            }}>{busyRow ? 'Working…' : action.type === 'revoke' ? 'Revoke access' : 'Confirm invitation'}</button>
            <button type="button" disabled={busyRow !== null} className="min-h-11 px-3 text-text-muted" onClick={() => { setAction(null); setReason(''); }}>Cancel</button>
          </div>
        </section>
      )}

      {/* Mobile: cards */}
      <div className="md:hidden space-y-2">
        {isLoading && <p className="rounded-lg border border-border bg-surface p-4 text-sm text-text-muted">Loading…</p>}
        {!isLoading && !isError && filtered.length === 0 && (
          <p className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">
            {total === 0 ? 'No portal users have been linked to a customer yet.' : 'No portal users match these filters.'}
          </p>
        )}
        {filtered.map((r) => (
          <div key={r.id} className="rounded-lg border border-border bg-surface p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-text truncate">{r.user.full_name ?? r.user.email}</p>
                <p className="text-xs text-text-muted break-all">{r.user.email}</p>
              </div>
              <StatusPill status={r.status} />
            </div>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <dt className="text-text-faint">Customer</dt>
              <dd className="text-text truncate">{customerNameById.get(r.customer_id) ?? 'Unknown customer'}</dd>
              <dt className="text-text-faint">Role</dt>
              <dd className="text-text">
                {r.role}
                {r.is_primary_contact && <PrimaryChip />}
              </dd>
              <dt className="text-text-faint">Last login</dt>
              <dd className="text-text">{r.last_login_at ? formatDateTime(r.last_login_at) : 'Never'}</dd>
              {r.disabled_reason && (
                <>
                  <dt className="text-text-faint">Note</dt>
                  <dd className="text-text"><DisableReasonNote reason={r.disabled_reason} /></dd>
                </>
              )}
            </dl>
            <RowActions row={r} busy={busyRow === r.id} canManage={canManage} onResend={(row) => { setReason(''); setAction({ type: 'invite', row }); }} onRevoke={(row) => { setReason(''); setAction({ type: 'revoke', row }); }} layout="stack" />
            <RowFeedbackLine feedback={feedback[r.id]} />
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block rounded-lg border border-border bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-3 text-xs text-text-muted">
            <tr>
              <th className="text-left px-4 py-2 font-medium">User</th>
              <th className="text-left px-4 py-2 font-medium">Customer</th>
              <th className="text-left px-4 py-2 font-medium">Role</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-left px-4 py-2 font-medium">Last login</th>
              <th className="text-left px-4 py-2 font-medium">Notes</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-sm text-text-muted">Loading…</td>
              </tr>
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-sm text-text-muted">
                  {total === 0 ? 'No portal users have been linked to a customer yet.' : 'No portal users match these filters.'}
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="align-top">
                <td className="px-4 py-2">
                  <div className="text-text">{r.user.full_name ?? r.user.email}</div>
                  <div className="text-xs text-text-muted">{r.user.email}</div>
                </td>
                <td className="px-4 py-2 text-text">{customerNameById.get(r.customer_id) ?? 'Unknown customer'}</td>
                <td className="px-4 py-2 text-text">
                  {r.role}
                  {r.is_primary_contact && <PrimaryChip />}
                </td>
                <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                <td className="px-4 py-2 text-text-muted text-xs whitespace-nowrap">
                  {r.last_login_at ? formatDateTime(r.last_login_at) : 'Never'}
                </td>
                <td className="px-4 py-2 text-xs text-text-muted">
                  {r.disabled_reason ? <DisableReasonNote reason={r.disabled_reason} /> : '—'}
                </td>
                <td className="px-4 py-2 text-right">
                  <RowActions row={r} busy={busyRow === r.id} canManage={canManage} onResend={(row) => { setReason(''); setAction({ type: 'invite', row }); }} onRevoke={(row) => { setReason(''); setAction({ type: 'revoke', row }); }} layout="inline" />
                  <RowFeedbackLine feedback={feedback[r.id]} align="right" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RowActions({
  row,
  busy,
  onResend,
  onRevoke,
  layout,
  canManage,
}: {
  row: CustomerUserRow;
  busy: boolean;
  canManage: boolean;
  onResend: (row: CustomerUserRow) => void;
  onRevoke: (row: CustomerUserRow) => void;
  layout: 'inline' | 'stack';
}) {
  const btn =
    'inline-flex items-center justify-center rounded-md text-xs font-medium min-h-[44px] md:min-h-[32px] px-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50';
  const wrap = layout === 'stack' ? 'grid grid-cols-1 gap-1.5 pt-1' : 'flex flex-wrap justify-end gap-1.5';
  return (
    <div className={wrap}>
      <a
        href={portalViewAsUrl(row.user_id)}
        target="_blank"
        rel="noopener noreferrer"
        className={`${btn} border border-border bg-surface text-text hover:bg-surface-strong`}
        aria-label={`Open the customer portal as ${row.user.email}`}
      >
        Open portal as this user
      </a>
      {canManage && row.status === 'invited' && (
        <button
          type="button"
          onClick={() => onResend(row)}
          disabled={busy}
          className={`${btn} border border-primary/40 bg-primary-soft text-primary hover:bg-surface-strong`}
        >
          {busy ? 'Working…' : 'Review invitation'}
        </button>
      )}
      {(row.status === 'revoked' || row.status === 'disabled') && <span className="text-xs text-text-muted">Reactivation requires a separate access review.</span>}
      {canManage && row.status !== 'revoked' && (
        <button
          type="button"
          onClick={() => onRevoke(row)}
          disabled={busy}
          className={`${btn} border border-error/40 text-error hover:bg-error-soft`}
        >
          Revoke
        </button>
      )}
    </div>
  );
}

function RowFeedbackLine({ feedback, align = 'left' }: { feedback?: RowFeedback; align?: 'left' | 'right' }) {
  if (!feedback) return null;
  return (
    <p
      role={feedback.tone === 'error' ? 'alert' : 'status'}
      className={`mt-1 text-xs break-words ${align === 'right' ? 'text-right' : ''} ${feedback.tone === 'error' ? 'text-error' : 'text-success'}`}
    >
      {feedback.message}
    </p>
  );
}

function PrimaryChip() {
  return (
    <span className="ml-1.5 rounded bg-primary-soft px-1 py-0.5 text-[10px] text-primary">primary</span>
  );
}

function StatusPill({ status }: { status: CustomerUserRow['status'] }) {
  const styles: Record<CustomerUserRow['status'], string> = {
    invited: 'bg-warning-soft text-warning',
    active: 'bg-success-soft text-success',
    disabled: 'bg-surface-3 text-text-muted',
    revoked: 'bg-error-soft text-error',
  };
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

function DisableReasonNote({ reason }: { reason: DisableReason }) {
  const labels: Record<DisableReason, string> = {
    zoho_status_change: 'Auto-disabled by Zoho status change',
    admin_revoked: 'Revoked by admin',
    never_logged_in: 'Invitation expired (never logged in)',
    security_policy: 'Disabled by security policy',
  };
  return <span>{labels[reason]}</span>;
}
